import type {Ability} from '../../../../generated/types';
import {isAbility} from './abilities';
import type {Catalog, SpellData} from './catalog';
import type {Character} from './character';
import type {CharacterState} from './character-state';
import type {CharacterSpell, SpellCasting} from './collect-effects';
import {DiceFormula, parseDice} from './dice';
import type {SpellSlot} from './spell-slots';

export type SpellCastingStats = {
	ability?: Ability;
	attackBonus?: number;
	saveDifficulty?: number;
	modifier: number;
};

export function spellCastingStats(
	catalog: Catalog,
	character: Character,
	spell: CharacterSpell,
	casting: SpellCasting,
): SpellCastingStats {
	const ability = castingAbility(catalog, character, spell, casting);
	const stats = ability ? character.spellStats(ability) : undefined;
	return {
		ability,
		attackBonus: casting.attackBonus ?? stats?.attackBonus,
		saveDifficulty: casting.saveDc ?? stats?.saveDifficulty,
		modifier: stats?.modifier ?? 0,
	};
}

function castingAbility(
	catalog: Catalog,
	character: Character,
	spell: CharacterSpell,
	casting: SpellCasting,
): Ability | undefined {
	const own = casting.ability || spell.ability;
	if (own && isAbility(own)) return own;
	const classIds = catalog.getSpell(spell.id)?.classIds || [];
	const sources = character.spellcasting.filter((source) => source.ability);
	return (sources.find((source) => classIds.includes(source.spellList)) || sources[0])?.ability;
}

export function availableSlots(character: Character, spellLevel: number): SpellSlot[] {
	return character.spellSlots.filter((slot) => slot.level >= spellLevel && slot.max > slot.spent);
}

export const canCastWithSlots = (character: Character, spellLevel: number) =>
	character.spellSlots.some((slot) => slot.level >= spellLevel);

export function spellDiceFormula(
	data: SpellData,
	castLevel: number,
	characterLevel: number,
	modifier: number,
): DiceFormula | undefined {
	const {dice} = data;
	if (!dice) return undefined;
	const bonus = dice.addsModifier ? modifier : 0;
	if (data.level === 0 && dice.byCharacterLevel) {
		const thresholds = Object.keys(dice.byCharacterLevel)
			.map(Number)
			.filter((level) => level <= characterLevel)
			.sort((first, second) => second - first);
		return parseDice(dice.byCharacterLevel[String(thresholds[0] ?? 1)] || dice.base, bonus);
	}
	const formula = parseDice(dice.base, bonus);
	const extraLevels = Math.max(0, castLevel - data.level);
	if (!dice.perSlot || !extraLevels) return formula;
	const extra = parseDice(dice.perSlot);
	const counts = new Map<number, number>();
	for (const die of [...formula.dice, ...extra.dice.map((die) => ({...die, count: die.count * extraLevels}))]) {
		counts.set(die.sides, (counts.get(die.sides) || 0) + die.count);
	}
	return {
		dice: [...counts].map(([sides, count]) => ({count, sides})),
		bonus: formula.bonus + extra.bonus * extraLevels,
	};
}

export function spendCastingPatch(
	state: CharacterState,
	casting: SpellCasting,
	slot: SpellSlot | undefined,
): Partial<CharacterState> {
	if (casting.kind === 'slot') {
		if (!slot) return {};
		return {spentSpellSlots: {...state.spentSpellSlots, [slot.key]: (state.spentSpellSlots[slot.key] || 0) + 1}};
	}
	const useKey = casting.kind === 'resource' ? casting.resourceKey! : casting.key;
	const cost = casting.kind === 'resource' ? casting.cost || 1 : 1;
	return {spentUses: {...state.spentUses, [useKey]: (state.spentUses[useKey] || 0) + cost}};
}

export function castingRemaining(character: Character, casting: SpellCasting, spellLevel: number): number {
	if (casting.kind === 'slot') {
		if (spellLevel === 0) return Infinity;
		return availableSlots(character, spellLevel).reduce((total, slot) => total + slot.max - slot.spent, 0);
	}
	const use = character.limitedUse(casting.kind === 'resource' ? casting.resourceKey! : casting.key);
	if (!use) return Infinity;
	const cost = casting.kind === 'resource' ? casting.cost || 1 : 1;
	return Math.floor((use.max - use.spent) / cost);
}
